import React, { useState } from 'react';
import {
  Box,
  Text,
  VStack,
  Heading,
  Grid,
  HStack,
  Button,
  Input,
  Textarea,
  Spinner
} from '@chakra-ui/react';
import { useContactForm, useSettings } from '../hooks/useApi';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: ''
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});

  const { submitForm, submitting, success, error, resetStatus } = useContactForm();
  const { data: settings, loading: settingsLoading } = useSettings();

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        return '';
      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!validateEmail(value)) return 'Please enter a valid email address';
        return '';
      case 'subject':
        if (!value.trim()) return 'Subject is required';
        if (value.trim().length < 3) return 'Subject must be at least 3 characters';
        return '';
      case 'message':
        if (!value.trim()) return 'Message is required';
        if (value.trim().length < 10) return 'Message must be at least 10 characters';
        return '';
      default:
        return '';
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Real-time validation for touched fields
    if (touched[name]) {
      const error = validateField(name, value);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }

    // Clear any previous form-level error/success when user starts typing
    if (error || success) {
      resetStatus();
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    // Validate on blur
    const error = validateField(name, value);
    setFieldErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched
    const allFields = ['name', 'email', 'subject', 'message'];
    setTouched(Object.fromEntries(allFields.map(field => [field, true])));

    // Validate all fields
    const errors = {};
    allFields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) errors[field] = error;
    });

    setFieldErrors(errors);

    // If there are errors, don't submit
    if (Object.keys(errors).length > 0) {
      return;
    }

    // Submit form
    await submitForm(formData);

    // Clear form on success
    if (success) {
      setFormData({
        name: '',
        email: '',
        company: '',
        subject: '',
        message: ''
      });
      setFieldErrors({});
      setTouched({});
    }
  };

  // Loading state
  if (settingsLoading) {
    return (
      <Box py={{ base: 16, md: 24, lg: 32 }} px={4}>
        <VStack spacing={12} maxW="5xl" mx="auto" textAlign="center">
          <Heading size={{ base: "xl", md: "2xl", lg: "4xl" }} textTransform="uppercase" letterSpacing="wide">
            GET IN TOUCH
          </Heading>
          <Spinner size="xl" color="green.500" thickness="4px" />
          <Text color="gray.300">Loading contact page...</Text>
        </VStack>
      </Box>
    );
  }

  // Get services array from settings - parse the contact_services_list field
  const services = settings?.contact_services_list 
    ? settings.contact_services_list.split('\n').filter(service => service.trim())
    : [];

  return (
    <Box py={{ base: 16, md: 24, lg: 32 }} px={4}>
      <VStack spacing={{ base: 16, md: 20, lg: 22 }} maxW="5xl" mx="auto">
        {/* Hero Section */}
        <VStack spacing={{ base: 8, md: 12 }} textAlign="center">
          <Heading size={{ base: "xl", md: "2xl", lg: "4xl" }} textTransform="uppercase" letterSpacing="wide">
            {settings?.contact_page_title || "GET IN TOUCH"}
          </Heading>
          <Text fontSize={{ base: "md", md: "lg", lg: "xl" }} color="gray.300" maxW="4xl" lineHeight="1.8">
            {settings?.contact_page_description ||
             "Ready to discuss your next project? We'd love to hear about your engineering challenges and explore how we can help build scalable solutions for your business."
            }
          </Text>
        </VStack>

        {/* Main Content Grid */}
        <Box py={{ base: 12, md: 16 }} w="100%">
          <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={{ base: 12, lg: 20 }} w="100%">
            {/* Contact Form */}
            <Box bg="gray.900" p={{ base: 6, md: 10, lg: 16 }} border="1px solid" borderColor="gray.700">
              <VStack spacing={{ base: 8, md: 12 }} align="start">
                <VStack spacing={6} align="start">
                  <HStack>
                    <Box w={3} h={3} bg="green.500" borderRadius="full" />
                    <Heading size={{ base: "md", md: "lg", lg: "xl" }} textTransform="uppercase" letterSpacing="wide">
                      {settings?.contact_form_title || "START A PROJECT"}
                    </Heading>
                  </HStack>
                  <Text color="gray.300" lineHeight="1.8" fontSize={{ base: "sm", md: "md", lg: "lg" }}>
                    {settings?.contact_form_description ||
                     "Tell us about your project requirements, timeline, and goals. We'll get back to you within 24 hours with a detailed proposal."
                    }
                  </Text>
                </VStack>

                <Box as="form" onSubmit={handleSubmit} w="100%">
                  <VStack spacing={{ base: 6, md: 8 }}>
                    {/* Name Field */}
                    <VStack spacing={3} align="start" w="100%">
                      <Text
                        color="white"
                        fontSize={{ base: "xs", md: "sm" }}
                        textTransform="uppercase"
                        letterSpacing="wide"
                        fontWeight="bold"
                      >
                        Full Name *
                      </Text>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        bg="gray.800"
                        border="1px solid"
                        borderColor={touched.name && fieldErrors.name ? "red.500" : "gray.600"}
                        color="white"
                        size={{ base: "md", md: "lg" }}
                        _hover={{
                          borderColor: touched.name && fieldErrors.name ? "red.400" : "gray.500"
                        }}
                        _focus={{
                          borderColor: touched.name && fieldErrors.name ? "red.500" : "green.500",
                          boxShadow: "none"
                        }}
                        placeholder="Your full name"
                        _placeholder={{ color: "gray.400" }}
                      />
                      {touched.name && fieldErrors.name && (
                        <Text color="red.300" fontSize={{ base: "xs", md: "sm" }}>
                          {fieldErrors.name}
                        </Text>
                      )}
                    </VStack>

                    {/* Email Field */}
                    <VStack spacing={3} align="start" w="100%">
                      <Text
                        color="white"
                        fontSize={{ base: "xs", md: "sm" }}
                        textTransform="uppercase"
                        letterSpacing="wide"
                        fontWeight="bold"
                      >
                        Email Address *
                      </Text>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        bg="gray.800"
                        border="1px solid"
                        borderColor={touched.email && fieldErrors.email ? "red.500" : "gray.600"}
                        color="white"
                        size={{ base: "md", md: "lg" }}
                        _hover={{
                          borderColor: touched.email && fieldErrors.email ? "red.400" : "gray.500"
                        }}
                        _focus={{
                          borderColor: touched.email && fieldErrors.email ? "red.500" : "green.500",
                          boxShadow: "none"
                        }}
                        placeholder="your@email.com"
                        _placeholder={{ color: "gray.400" }}
                      />
                      {touched.email && fieldErrors.email && (
                        <Text color="red.300" fontSize={{ base: "xs", md: "sm" }}>
                          {fieldErrors.email}
                        </Text>
                      )}
                    </VStack>

                    {/* Company Field */}
                    <VStack spacing={3} align="start" w="100%">
                      <Text
                        color="white"
                        fontSize={{ base: "xs", md: "sm" }}
                        textTransform="uppercase"
                        letterSpacing="wide"
                        fontWeight="bold"
                      >
                        Company
                      </Text>
                      <Input
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        bg="gray.800"
                        border="1px solid"
                        borderColor="gray.600"
                        color="white"
                        size={{ base: "md", md: "lg" }}
                        _hover={{ borderColor: "gray.500" }}
                        _focus={{ borderColor: "green.500", boxShadow: "none" }}
                        placeholder="Your company name"
                        _placeholder={{ color: "gray.400" }}
                      />
                    </VStack>

                    {/* Subject Field */}
                    <VStack spacing={3} align="start" w="100%">
                      <Text
                        color="white"
                        fontSize={{ base: "xs", md: "sm" }}
                        textTransform="uppercase"
                        letterSpacing="wide"
                        fontWeight="bold"
                      >
                        Subject *
                      </Text>
                      <Input
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        bg="gray.800"
                        border="1px solid"
                        borderColor={touched.subject && fieldErrors.subject ? "red.500" : "gray.600"}
                        color="white"
                        size={{ base: "md", md: "lg" }}
                        _hover={{
                          borderColor: touched.subject && fieldErrors.subject ? "red.400" : "gray.500"
                        }}
                        _focus={{
                          borderColor: touched.subject && fieldErrors.subject ? "red.500" : "green.500",
                          boxShadow: "none"
                        }}
                        placeholder="Project subject or topic"
                        _placeholder={{ color: "gray.400" }}
                      />
                      {touched.subject && fieldErrors.subject && (
                        <Text color="red.300" fontSize={{ base: "xs", md: "sm" }}>
                          {fieldErrors.subject}
                        </Text>
                      )}
                    </VStack>

                    {/* Message Field */}
                    <VStack spacing={3} align="start" w="100%">
                      <Text
                        color="white"
                        fontSize={{ base: "xs", md: "sm" }}
                        textTransform="uppercase"
                        letterSpacing="wide"
                        fontWeight="bold"
                      >
                        Project Details *
                      </Text>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        bg="gray.800"
                        border="1px solid"
                        borderColor={touched.message && fieldErrors.message ? "red.500" : "gray.600"}
                        color="white"
                        size={{ base: "md", md: "lg" }}
                        _hover={{
                          borderColor: touched.message && fieldErrors.message ? "red.400" : "gray.500"
                        }}
                        _focus={{
                          borderColor: touched.message && fieldErrors.message ? "red.500" : "green.500",
                          boxShadow: "none"
                        }}
                        placeholder="Tell us about your project, timeline, and requirements..."
                        _placeholder={{ color: "gray.400" }}
                        rows={{ base: 4, md: 6 }}
                        resize="vertical"
                      />
                      {touched.message && fieldErrors.message && (
                        <Text color="red.300" fontSize={{ base: "xs", md: "sm" }}>
                          {fieldErrors.message}
                        </Text>
                      )}
                    </VStack>

                    <Button
                      type="submit"
                      bg="green.500"
                      color="white"
                      size={{ base: "md", md: "lg" }}
                      w="100%"
                      py={{ base: 4, md: 6 }}
                      _hover={{ bg: "green.600" }}
                      textTransform="uppercase"
                      letterSpacing="wide"
                      fontWeight="bold"
                      fontSize={{ base: "sm", md: "md" }}
                      isLoading={submitting}
                      loadingText="Sending Message..."
                      isDisabled={Object.keys(fieldErrors).some(key => fieldErrors[key])}
                    >
                      Send Message
                    </Button>

                    {/* Success Message */}
                    {success && (
                      <Box bg="green.900" border="1px solid" borderColor="green.600" p={6} borderRadius="md" w="100%">
                        <Text color="green.300" textAlign="center" fontWeight="bold" fontSize={{ base: "md", md: "lg" }}>
                          ✅ Message sent successfully! We'll get back to you within 24 hours.
                        </Text>
                      </Box>
                    )}

                    {/* Error Message */}
                    {error && (
                      <Box bg="red.900" border="1px solid" borderColor="red.600" p={6} borderRadius="md" w="100%">
                        <Text color="red.300" textAlign="center" fontWeight="bold" fontSize={{ base: "md", md: "lg" }}>
                          ❌ {error}
                        </Text>
                      </Box>
                    )}
                  </VStack>
                </Box>
              </VStack>
            </Box>

            {/* Contact Information */}
            <VStack spacing={{ base: 12, md: 16 }} align="start">
              {/* Contact Details */}
              <Box bg="gray.900" p={{ base: 6, md: 10, lg: 16 }} border="1px solid" borderColor="gray.700" w="100%">
                <VStack spacing={8} align="start">
                  <VStack spacing={6} align="start">
                    <HStack>
                      <Box w={3} h={3} bg="green.500" borderRadius="full" />
                      <Heading size={{ base: "md", md: "lg", lg: "xl" }} textTransform="uppercase" letterSpacing="wide">
                        {settings?.contact_info_title || "CONTACT INFO"}
                      </Heading>
                    </HStack>
                  </VStack>

                  <VStack spacing={6} align="start" w="100%">
                    <VStack spacing={3} align="start">
                      <Text fontSize={{ base: "xs", md: "sm" }} textTransform="uppercase" letterSpacing="wide" color="green.500" fontWeight="bold">
                        EMAIL
                      </Text>
                      <Text color="white" fontSize={{ base: "md", md: "lg" }}>
                        {settings?.contact_info_email || "hello@cloverridgetech.com"}
                      </Text>
                    </VStack>

                    <VStack spacing={3} align="start">
                      <Text fontSize={{ base: "xs", md: "sm" }} textTransform="uppercase" letterSpacing="wide" color="green.500" fontWeight="bold">
                        LOCATION
                      </Text>
                      <Text color="white" fontSize={{ base: "md", md: "lg" }} whiteSpace="pre-line">
                        {settings?.contact_info_location || "Remote Team\nUnited States"}
                      </Text>
                    </VStack>

                    <VStack spacing={3} align="start">
                      <Text fontSize={{ base: "xs", md: "sm" }} textTransform="uppercase" letterSpacing="wide" color="green.500" fontWeight="bold">
                        RESPONSE TIME
                      </Text>
                      <Text color="white" fontSize={{ base: "md", md: "lg" }}>
                        {settings?.contact_info_response_time || "Within 24 hours"}
                      </Text>
                    </VStack>
                  </VStack>
                </VStack>
              </Box>

              {/* Services */}
              <Box bg="gray.900" p={{ base: 6, md: 10, lg: 16 }} border="1px solid" borderColor="gray.700" w="100%">
                <VStack spacing={8} align="start">
                  <VStack spacing={6} align="start">
                    <HStack>
                      <Box w={3} h={3} bg="green.500" borderRadius="full" />
                      <Heading size={{ base: "md", md: "lg", lg: "xl" }} textTransform="uppercase" letterSpacing="wide">
                        {settings?.contact_services_title || "SERVICES"}
                      </Heading>
                    </HStack>
                  </VStack>

                  <VStack spacing={4} align="start" w="100%">
                    {services.map((service, index) => (
                      <HStack key={index}>
                        <Box w={2} h={2} bg="green.500" borderRadius="full" />
                        <Text color="gray.300" fontSize={{ base: "sm", md: "md" }}>{service}</Text>
                      </HStack>
                    ))}
                  </VStack>
                </VStack>
              </Box>
            </VStack>
          </Grid>
        </Box>

        {/* Call to Action */}
        <Box py={{ base: 12, md: 16 }} w="100%">
          <VStack spacing={{ base: 8, md: 12 }} textAlign="center" w="100%">
            <Heading size={{ base: "lg", md: "xl", lg: "3xl" }} textTransform="uppercase" letterSpacing="wide">
              {settings?.contact_cta_title || "READY TO BUILD SOMETHING AMAZING?"}
            </Heading>
            <Text fontSize={{ base: "md", md: "lg", lg: "xl" }} color="gray.300" maxW="3xl" lineHeight="1.8">
              {settings?.contact_cta_description ? (
                settings.contact_cta_description.includes("free consultation") ? (
                  <>
                    {settings.contact_cta_description.split("free consultation")[0]}
                    <Box
                      as="a"
                      href="https://learning.cloverridgetech.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      color="green.500"
                      _hover={{ color: "green.400", textDecoration: "underline" }}
                      transition="color 0.2s"
                    >
                      free consultation
                    </Box>
                    {settings.contact_cta_description.split("free consultation")[1]}
                  </>
                ) : settings.contact_cta_description
              ) : "Whether you're a startup looking to build your first application or an enterprise needing to scale your infrastructure, we're here to help you achieve your engineering goals."}
            </Text>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
};

export default Contact;